import React, { useState, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, QrCode, Plus, Check, ShoppingBag, ArrowRight, ArrowLeft, User, Users, HeartHandshake, Shirt, UtensilsCrossed, Flame, Gem, Nut, Package as PackageIcon, Sparkles, Trophy, CreditCard, Lock } from 'lucide-react';
import { translations, pujaPackages } from '../mock';
import { useLang } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import { useToast } from '../hooks/use-toast';
import api, { formatApiError } from '../lib/api';

const iconMap = { User, Users, HeartHandshake, Shirt, UtensilsCrossed, Flame, Gem, Nut, Package: PackageIcon, Sparkles, Trophy };

export default function Participate() {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const { t, lang } = useLang();
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { pujas, samagri: samagriList, settings, refreshBookings } = useData();
  const puja = pujas.find((p) => p.slug === slug);

  const effectivePackages = (puja && puja.packages && puja.packages.length) ? puja.packages : pujaPackages;
  const initialPkg = sp.get('package') || effectivePackages[Math.min(2, effectivePackages.length - 1)]?.id || effectivePackages[0]?.id;
  const [pkgId, setPkgId] = useState(initialPkg);
  const [selectedSamagri, setSelectedSamagri] = useState({});
  const [panditDakshina, setPanditDakshina] = useState(false);
  const [prasadDelivery, setPrasadDelivery] = useState(false);
  const PANDIT_DAKSHINA_PRICE = 151;
  const PRASAD_DELIVERY_PRICE = 149;
  const [form, setForm] = useState({
    name: user?.name || '',
    gotra: '',
    phone: '',
    whatsapp: '',
    email: user?.email || '',
    // Address only required if Prasad delivery is enabled in Step 3
    address1: '',
    address2: '',
    pincode: '',
    city: '',
    state: '',
  });
  const [step, setStep] = useState(1); // 1: Package, 2: Samagri, 3: Details, 4: Payment
  const [done, setDone] = useState(false);
  const [paying, setPaying] = useState(false);

  // samagri comes from DataContext (backend + fallback). Prasad is handled
  // separately as a Step-3 toggle so we filter it out of the Samagri list.
  const shopSamagri = useMemo(
    () => (samagriList || []).filter((s) => !String(s.id || '').toLowerCase().includes('prasad')),
    [samagriList]
  );

  const pkg = effectivePackages.find((p) => p.id === pkgId) || effectivePackages[0];
  const qrImg = settings.qr;
  const razorpayEnabled = settings.razorpayEnabled && settings.razorpayKeyId;

  // Auto-detect city + state from pincode using free postalpincode.in API
  const detectPincode = async (pin) => {
    if (!/^\d{6}$/.test(pin)) return;
    try {
      const res = await fetch(`https://api.postalpincode.in/pincode/${pin}`);
      const j = await res.json();
      const po = j?.[0]?.PostOffice?.[0];
      if (po) {
        setForm((prev) => ({ ...prev, city: po.District || po.Block || '', state: po.State || '' }));
      }
    } catch { /* ignore — user can fill manually */ }
  };

  const total = useMemo(() => {
    const samagriTotal = Object.entries(selectedSamagri)
      .filter(([_, v]) => v)
      .reduce((sum, [id]) => sum + (shopSamagri.find((s) => s.id === id)?.price || 0), 0);
    return (
      (pkg?.price || 0) +
      samagriTotal +
      (panditDakshina ? PANDIT_DAKSHINA_PRICE : 0) +
      (prasadDelivery ? PRASAD_DELIVERY_PRICE : 0)
    );
  }, [pkgId, selectedSamagri, shopSamagri, pkg, panditDakshina, prasadDelivery]);

  const toggleSamagri = (id) => setSelectedSamagri((prev) => ({ ...prev, [id]: !prev[id] }));

  const goDetails = (e) => {
    e.preventDefault();
    if (!form.name || !form.gotra || !form.phone || !form.whatsapp || !form.email) {
      toast({ title: lang === 'en' ? 'Please fill all required fields' : 'सभी आवश्यक फ़ील्ड भरें', variant: 'destructive' });
      return;
    }
    if (prasadDelivery && (!form.address1 || !form.pincode || !form.city || !form.state)) {
      toast({ title: lang === 'en' ? 'Please complete your delivery address for Prasad' : 'प्रसाद हेतु डिलीवरी पता पूरा करें', variant: 'destructive' });
      return;
    }
    setStep(4);
  };

  // Creates the booking (pending) on the backend and returns its id
  const createPendingBooking = async () => {
    const chosenSamagri = shopSamagri.filter((s) => selectedSamagri[s.id]);
    const addOns = [];
    if (panditDakshina) addOns.push({ id: 'pandit_dakshina', name: 'Pandit Ji Dakshina', price: PANDIT_DAKSHINA_PRICE });
    if (prasadDelivery) addOns.push({ id: 'prasad', name: 'Prasad Home Delivery', price: PRASAD_DELIVERY_PRICE });
    const fullAddress = prasadDelivery
      ? [form.address1, form.address2, form.city, form.state, form.pincode].filter(Boolean).join(', ')
      : '';
    const { data } = await api.post('/api/bookings', {
      pujaSlug: slug,
      pujaTitle: puja.title_en,
      package: pkg.name_en,
      amount: total,
      samagri: [
        ...chosenSamagri.map((s) => ({ id: s.id, name: s.name_en, price: s.price })),
        ...addOns,
      ],
      user: {
        ...form,
        address: fullAddress,
        panditDakshina,
        prasadDelivery,
        packageId: pkgId,
        paymentMethod: 'pending',
      },
    });
    return data;
  };

  const payWithRazorpay = async () => {
    if (!window.Razorpay) {
      toast({ title: 'Razorpay checkout not loaded. Try again.', variant: 'destructive' });
      return;
    }
    setPaying(true);
    let booking;
    let order;
    try {
      booking = await createPendingBooking();
      const orderRes = await api.post('/api/payments/create-order', {
        amount: total,
        bookingId: booking.id,
        receipt: booking.id,
        notes: { puja: puja.title_en, gotra: form.gotra, name: form.name },
      });
      order = orderRes.data;
    } catch (err) {
      setPaying(false);
      toast({ title: formatApiError(err), variant: 'destructive' });
      return;
    }
    const opts = {
      key: order.keyId,
      amount: order.amount,
      currency: order.currency || 'INR',
      order_id: order.orderId,
      name: 'Navavidha Bhakti',
      description: `${pkg.name_en} - ${puja.title_en}`.slice(0, 80),
      image: '/brand/logo.png',
      handler: async (response) => {
        try {
          await api.post('/api/payments/verify', {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            bookingId: booking.id,
          });
          await refreshBookings();
          setPaying(false);
          setDone(true);
          toast({ title: 'Payment successful! 🙏' });
        } catch (err) {
          setPaying(false);
          toast({ title: formatApiError(err) || 'Payment verification failed', variant: 'destructive' });
        }
      },
      prefill: { name: form.name, email: form.email, contact: form.phone },
      notes: { puja: puja.title_en, gotra: form.gotra },
      theme: { color: '#D9701F' },
      modal: {
        ondismiss: () => {
          setPaying(false);
          toast({ title: 'Payment cancelled' });
        },
      },
    };
    try {
      const rzp = new window.Razorpay(opts);
      rzp.on('payment.failed', (resp) => {
        setPaying(false);
        toast({ title: `Payment failed: ${resp.error?.description || 'Unknown error'}`, variant: 'destructive' });
      });
      rzp.open();
    } catch (err) {
      setPaying(false);
      toast({ title: `Error: ${err.message}`, variant: 'destructive' });
    }
  };

  const confirmManualPayment = async () => {
    try {
      await createPendingBooking();
      await refreshBookings();
      setDone(true);
      toast({ title: lang === 'en' ? 'Booking Confirmed!' : 'बुकिंग सफल!' });
    } catch (err) {
      toast({ title: formatApiError(err), variant: 'destructive' });
    }
  };

  if (!puja) return <div className="text-center py-20">Puja not found</div>;

  if (done) {
    return (
      <div className="max-w-xl mx-auto px-6 py-16 text-center">
        <div className="w-20 h-20 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-6">
          <CheckCircle2 className="w-10 h-10 text-green-600" />
        </div>
        <h1 className="text-3xl font-bold mb-3">{lang === 'en' ? 'Booking Confirmed!' : 'बुकिंग सफल!'}</h1>
        <p className="text-stone-600 mb-6">
          {lang === 'en'
            ? `Thank you ${form.name}. Your Sankalp for ${pkg.name_en} has been recorded. Pandit ji will perform the puja and video will be shared.`
            : `धन्यवाद ${form.name}। ${pkg.name_hi} के लिए आपका संकल्प पंजीकृत हो गया है।`}
        </p>
        <button onClick={() => navigate('/')} className="btn-orange rounded-full px-6 py-3 font-semibold">
          {lang === 'en' ? 'Back to Home' : 'घर वापस'}
        </button>
      </div>
    );
  }

  const steps = [
    { id: 1, label_en: 'Package', label_hi: 'पैकेज' },
    { id: 2, label_en: 'Samagri', label_hi: 'सामग्री' },
    { id: 3, label_en: 'Details', label_hi: 'विवरण' },
    { id: 4, label_en: 'Payment', label_hi: 'भुगतान' }
  ];

  return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      {/* Stepper */}
      <div className="flex items-center gap-2 mb-8">
        {steps.map((s, i) => (
          <React.Fragment key={s.id}>
            <div className="flex flex-col items-center flex-1">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition ${
                step >= s.id ? 'bg-orange-600 text-white' : 'bg-orange-100 text-orange-500'
              }`}>
                {step > s.id ? <Check className="w-4 h-4" /> : s.id}
              </div>
              <div className={`text-xs mt-1 font-medium ${step >= s.id ? 'text-orange-700' : 'text-stone-400'}`}>
                {lang === 'en' ? s.label_en : s.label_hi}
              </div>
            </div>
            {i < steps.length - 1 && (
              <div className={`h-0.5 flex-1 -mt-5 transition ${step > s.id ? 'bg-orange-600' : 'bg-orange-100'}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-orange-100 shadow-sm p-6 md:p-8">
          <h1 className="text-xl md:text-2xl font-bold text-stone-900 mb-1 line-clamp-2">{lang === 'en' ? puja.title_en : puja.title_hi}</h1>
          <p className="text-sm text-stone-500 mb-6">{lang === 'en' ? puja.date_label_en : puja.date_label_hi}</p>

          {/* Step 1: Package */}
          {step === 1 && (
            <div>
              <h2 className="text-lg font-semibold text-orange-800 mb-4">{lang === 'en' ? 'Choose Your Package' : 'अपना पैकेज चुनें'}</h2>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                {effectivePackages.map((p) => {
                  const Icon = iconMap[p.icon] || User;
                  const isSel = pkgId === p.id;
                  return (
                    <button
                      key={p.id}
                      onClick={() => setPkgId(p.id)}
                      className={`relative text-left rounded-xl border-2 p-4 transition ${
                        isSel ? 'border-orange-500 bg-orange-50/50 shadow-md' : 'border-orange-100 hover:border-orange-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${isSel ? 'bg-orange-500 text-white' : 'bg-orange-100 text-orange-700'}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-stone-900 text-sm md:text-[15px] leading-tight">{lang === 'en' ? p.name_en : p.name_hi}</div>
                          <div className="text-xs text-stone-500 mt-0.5">{p.persons} {lang === 'en' ? 'name(s)' : 'नाम'}</div>
                        </div>
                        <div className="text-lg font-bold text-orange-700 shrink-0">₹{p.price}</div>
                      </div>
                      {isSel && <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-orange-500 text-white flex items-center justify-center"><Check className="w-3 h-3" /></div>}
                    </button>
                  );
                })}
              </div>
              <div className="mt-6 flex justify-end">
                <button onClick={() => setStep(2)} className="btn-orange rounded-full px-6 py-3 font-semibold flex items-center gap-2">
                  {lang === 'en' ? 'Add Samagri' : 'सामग्री जोड़ें'} <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Samagri */}
          {step === 2 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-lg font-semibold text-orange-800 flex items-center gap-2">
                  <ShoppingBag className="w-5 h-5" /> {lang === 'en' ? 'Add Puja Samagri & Offerings' : 'पूजा सामग्री जोड़ें'}
                </h2>
                <span className="text-xs text-stone-500">{lang === 'en' ? 'Optional' : 'वैकल्पिक'}</span>
              </div>
              <p className="text-sm text-stone-600 mb-5">
                {lang === 'en'
                  ? 'Enhance your puja by adding these sacred offerings. All items will be offered in your name.'
                  : 'आपके नाम से अर्पित की जाने वाली पवित्र वस्तुएं जोड़ें।'}
              </p>

              {/* Featured add-on: Pandit Ji Dakshina */}
              <button
                type="button"
                onClick={() => setPanditDakshina((v) => !v)}
                data-testid="pandit-dakshina-toggle"
                className={`w-full text-left rounded-xl border-2 p-4 flex gap-3 items-center transition relative mb-5 ${
                  panditDakshina
                    ? 'border-orange-500 bg-gradient-to-br from-orange-50 to-amber-50 shadow-md'
                    : 'border-dashed border-orange-300 bg-orange-50/40 hover:border-orange-400'
                }`}
              >
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center shrink-0 ${
                  panditDakshina ? 'bg-gradient-to-br from-orange-500 to-red-500 text-white' : 'bg-orange-100 text-orange-700'
                }`}>
                  <HeartHandshake className="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-sm text-stone-900">
                      {lang === 'en' ? 'Pandit Ji Ka Dakshina' : 'पंडित जी का दक्षिणा'}
                    </span>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-orange-500 text-white uppercase tracking-wider">
                      {lang === 'en' ? 'Recommended' : 'अनुशंसित'}
                    </span>
                  </div>
                  <div className="text-xs text-stone-600 mt-0.5 line-clamp-2">
                    {lang === 'en'
                      ? 'Offer dakshina directly to Pandit Ji for his blessings and seva.'
                      : 'पंडित जी को उनकी सेवा हेतु सीधे दक्षिणा अर्पण करें।'}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-bold text-orange-700">+₹{PANDIT_DAKSHINA_PRICE}</div>
                  <div className={`mt-1 w-6 h-6 rounded-md border-2 flex items-center justify-center ml-auto ${
                    panditDakshina ? 'bg-orange-500 border-orange-500 text-white' : 'border-orange-300 bg-white'
                  }`}>
                    {panditDakshina ? <Check className="w-4 h-4" /> : <Plus className="w-3.5 h-3.5 text-orange-400" />}
                  </div>
                </div>
              </button>

              <div className="grid sm:grid-cols-2 gap-3">
                {shopSamagri.map((s) => {
                  const Icon = iconMap[s.icon] || Sparkles;
                  const sel = !!selectedSamagri[s.id];
                  return (
                    <button
                      key={s.id}
                      onClick={() => toggleSamagri(s.id)}
                      className={`text-left rounded-xl border-2 p-3 flex gap-3 items-center transition relative ${
                        sel ? 'border-orange-500 bg-orange-50/60 shadow-sm' : 'border-orange-100 hover:border-orange-300 bg-white'
                      }`}
                    >
                      {s.image ? (
                        <img src={s.image} alt={s.name_en} className={`w-14 h-14 rounded-xl object-cover shrink-0 border ${sel ? 'border-orange-400' : 'border-orange-100'}`} />
                      ) : (
                        <div className={`w-14 h-14 rounded-xl flex items-center justify-center shrink-0 ${sel ? 'bg-gradient-to-br from-orange-500 to-red-500 text-white' : 'bg-orange-100 text-orange-700'}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm text-stone-900 truncate">{lang === 'en' ? s.name_en : s.name_hi}</div>
                        <div className="text-xs text-stone-500 line-clamp-2">{lang === 'en' ? s.desc_en : s.desc_hi}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-sm font-bold text-orange-700">+₹{s.price}</div>
                        <div className={`mt-1 w-6 h-6 rounded-md border-2 flex items-center justify-center ml-auto ${sel ? 'bg-orange-500 border-orange-500 text-white' : 'border-orange-300 bg-white'}`}>
                          {sel ? <Check className="w-4 h-4" /> : <Plus className="w-3.5 h-3.5 text-orange-400" />}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="mt-6 flex justify-between">
                <button onClick={() => setStep(1)} className="px-5 py-2.5 rounded-full border border-orange-300 text-orange-700 font-semibold flex items-center gap-2">
                  <ArrowLeft className="w-4 h-4" /> {lang === 'en' ? 'Back' : 'वापस'}
                </button>
                <button onClick={() => setStep(3)} className="btn-orange rounded-full px-6 py-3 font-semibold flex items-center gap-2">
                  {lang === 'en' ? 'Continue' : 'आगे बढ़ें'} <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Details */}
          {step === 3 && (
            <form onSubmit={goDetails} className="space-y-5">
              <h2 className="text-lg font-semibold text-orange-800">{t(translations.puja.fillDetails)}</h2>
              <div className="grid md:grid-cols-3 gap-4">
                {[
                  { key: 'name', label: translations.puja.fullName },
                  { key: 'gotra', label: translations.puja.gotra },
                  { key: 'phone', label: { en: 'Calling Number', hi: 'कॉलिंग नंबर' }, type: 'tel' },
                  { key: 'whatsapp', label: { en: 'WhatsApp Number', hi: 'व्हाट्सएप नंबर' }, type: 'tel' },
                  { key: 'email', label: translations.puja.email, type: 'email' },
                ].map((f) => (
                  <div key={f.key}>
                    <label className="text-sm font-medium text-stone-700 mb-1 block">{t(f.label)}</label>
                    <input
                      type={f.type || 'text'}
                      value={form[f.key]}
                      onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                      required
                      className="w-full border border-orange-200 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-orange-400"
                    />
                  </div>
                ))}
              </div>

              {/* Prasad Home Delivery toggle — user enables here; address form appears when ON */}
              <div className={`rounded-2xl border-2 p-4 md:p-5 transition ${
                prasadDelivery ? 'border-orange-500 bg-gradient-to-br from-orange-50 to-amber-50' : 'border-dashed border-orange-300 bg-orange-50/30'
              }`} data-testid="prasad-toggle-block">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
                      prasadDelivery ? 'bg-gradient-to-br from-orange-500 to-red-500 text-white' : 'bg-orange-100 text-orange-700'
                    }`}>
                      <PackageIcon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-bold text-stone-900">
                        {lang === 'en' ? 'Add Prasad Home Delivery' : 'प्रसाद घर डिलीवरी जोड़ें'}
                        <span className="ml-2 text-sm font-bold text-orange-700">+₹{PRASAD_DELIVERY_PRICE}</span>
                      </h3>
                      <p className="text-xs text-nv-muted mt-0.5">
                        {lang === 'en'
                          ? 'Blessed Prasad delivered to your home within 7-10 days. Enable this to enter your delivery address.'
                          : 'आशीर्वादित प्रसाद 7-10 दिन में आपके घर। इसे enable करने पर डिलीवरी पता माँगा जाएगा।'}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={prasadDelivery}
                    onClick={() => setPrasadDelivery((v) => !v)}
                    data-testid="prasad-toggle"
                    className={`relative w-14 h-7 rounded-full transition-colors shrink-0 ${
                      prasadDelivery ? 'bg-orange-500' : 'bg-stone-300'
                    }`}
                  >
                    <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transform transition-transform ${
                      prasadDelivery ? 'translate-x-7' : ''
                    }`} />
                  </button>
                </div>

                {prasadDelivery && (
                  <div className="mt-4 pt-4 border-t border-orange-200" data-testid="prasad-address-block">
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="md:col-span-3">
                        <label className="text-sm font-medium text-stone-700 mb-1 block">
                          {lang === 'en' ? 'Address Line 1' : 'पता पंक्ति 1'}
                        </label>
                        <input
                          type="text"
                          value={form.address1}
                          onChange={(e) => setForm({ ...form, address1: e.target.value })}
                          required
                          placeholder={lang === 'en' ? 'House / flat no, street' : 'मकान / फ्लैट नं., गली'}
                          data-testid="address-line-1"
                          className="w-full border border-orange-200 rounded-lg px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-orange-400"
                        />
                      </div>
                      <div className="md:col-span-3">
                        <label className="text-sm font-medium text-stone-700 mb-1 block">
                          {lang === 'en' ? 'Address Line 2' : 'पता पंक्ति 2'}
                        </label>
                        <input
                          type="text"
                          value={form.address2}
                          onChange={(e) => setForm({ ...form, address2: e.target.value })}
                          placeholder={lang === 'en' ? 'Landmark, area (optional)' : 'लैंडमार्क, क्षेत्र (वैकल्पिक)'}
                          data-testid="address-line-2"
                          className="w-full border border-orange-200 rounded-lg px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-orange-400"
                        />
                      </div>

                      <div>
                        <label className="text-sm font-medium text-stone-700 mb-1 block">
                          {lang === 'en' ? 'PIN Code' : 'पिन कोड'}
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          maxLength={6}
                          value={form.pincode}
                          onChange={(e) => {
                            const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                            setForm((p) => ({ ...p, pincode: v }));
                            if (v.length === 6) detectPincode(v);
                          }}
                          required
                          placeholder="6-digit PIN"
                          data-testid="pincode"
                          className="w-full border border-orange-200 rounded-lg px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-orange-400"
                        />
                      </div>

                      <div>
                        <label className="text-xs font-semibold text-orange-600 mb-1 block uppercase tracking-wide">
                          {lang === 'en' ? 'Automatic Detect' : 'स्वतः पहचान'}
                        </label>
                        <input
                          type="text"
                          value={form.city}
                          onChange={(e) => setForm({ ...form, city: e.target.value })}
                          required
                          placeholder={lang === 'en' ? 'City / District' : 'शहर / ज़िला'}
                          data-testid="city"
                          className="w-full border border-orange-200 rounded-lg px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-orange-400"
                        />
                      </div>

                      <div>
                        <label className="text-xs font-semibold text-orange-600 mb-1 block uppercase tracking-wide">
                          {lang === 'en' ? 'Automatic Detect' : 'स्वतः पहचान'}
                        </label>
                        <input
                          type="text"
                          value={form.state}
                          onChange={(e) => setForm({ ...form, state: e.target.value })}
                          required
                          placeholder={lang === 'en' ? 'State' : 'राज्य'}
                          data-testid="state"
                          className="w-full border border-orange-200 rounded-lg px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-orange-400"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-between items-center pt-2">
                <button type="button" onClick={() => setStep(2)} className="px-5 py-2.5 rounded-full border border-orange-300 text-orange-700 font-semibold flex items-center gap-2">
                  <ArrowLeft className="w-4 h-4" /> {lang === 'en' ? 'Back' : 'वापस'}
                </button>
                <button type="submit" className="btn-orange rounded-full px-6 py-3 font-semibold flex items-center gap-2">
                  {lang === 'en' ? 'Proceed to Payment' : 'भुगतान पर'} <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </form>
          )}

          {/* Step 4: Payment */}
          {step === 4 && (
            <div>
              <h2 className="text-lg font-semibold text-orange-800 mb-2 flex items-center gap-2">
                <CreditCard className="w-5 h-5" /> {lang === 'en' ? 'Complete Payment' : 'भुगतान पूरा करें'}
              </h2>

              {razorpayEnabled ? (
                <>
                  <div className="bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-200 rounded-2xl p-6 mb-4">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center text-white">
                        <CreditCard className="w-6 h-6" />
                      </div>
                      <div>
                        <div className="font-bold text-stone-900">{lang === 'en' ? 'Pay Securely with Razorpay' : 'रज़रपे से सुरक्षित भुगतान'}</div>
                        <div className="text-xs text-stone-600">UPI · Cards · Netbanking · Wallets</div>
                      </div>
                    </div>
                    <div className="flex items-baseline justify-between mb-4">
                      <span className="text-sm text-stone-600">{lang === 'en' ? 'Total Amount' : 'कुल राशि'}</span>
                      <span className="text-3xl font-bold text-orange-700">₹ {total}</span>
                    </div>
                    <button onClick={payWithRazorpay} disabled={paying} className="btn-orange w-full rounded-lg py-3.5 font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
                      {paying ? <>{lang === 'en' ? 'Opening...' : 'खुल रहा है...'}</> : <><Lock className="w-4 h-4" /> {lang === 'en' ? `Pay ₹${total} Now` : `₹${total} भुगतान करें`}</>}
                    </button>
                    <p className="text-[10px] text-stone-500 mt-3 text-center flex items-center justify-center gap-1">
                      <Lock className="w-3 h-3" /> {lang === 'en' ? '256-bit SSL encrypted · Razorpay Secure' : '256-बिट SSL एन्क्रिप्टेड · रज़रपे सुरक्षित'} · {settings.razorpayMode?.toUpperCase()} MODE
                    </p>
                  </div>
                  <details className="bg-stone-50 border border-stone-200 rounded-xl p-3 text-sm">
                    <summary className="cursor-pointer font-medium text-stone-700">{lang === 'en' ? 'Prefer UPI QR? Click here' : 'UPI QR पसंद है? यहाँ क्लिक करें'}</summary>
                    <div className="mt-3 flex flex-col items-center">
                      <img src={qrImg} alt="UPI QR" className="w-44 h-44 bg-white p-2 rounded-xl border border-orange-200 object-contain" />
                      <p className="text-xs text-stone-600 mt-2">UPI: {settings.upiId}</p>
                      <button onClick={confirmManualPayment} className="mt-3 px-5 py-2 rounded-lg border-2 border-orange-500 text-orange-700 text-sm font-semibold hover:bg-orange-50">
                        {lang === 'en' ? "I've paid manually via UPI" : 'मैंने UPI से भुगतान कर दिया है'}
                      </button>
                    </div>
                  </details>
                </>
              ) : (
                <>
                  <p className="text-sm text-stone-600 mb-6">{t(translations.puja.payInstr)}</p>
                  <div className="flex flex-col items-center bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200 rounded-2xl p-6">
                    <img src={qrImg} alt="UPI QR" className="w-56 h-56 bg-white p-3 rounded-xl shadow-lg object-contain" />
                    <div className="mt-4 text-center">
                      <div className="text-sm text-stone-600">{lang === 'en' ? 'Amount to pay' : 'भुगतान राशि'}</div>
                      <div className="text-3xl font-bold text-orange-700">₹ {total}</div>
                      <div className="text-xs text-stone-500 mt-2">UPI: {settings.upiId}</div>
                    </div>
                  </div>
                </>
              )}

              <div className="flex justify-between mt-6">
                <button onClick={() => setStep(3)} className="px-5 py-2.5 rounded-full border border-orange-300 text-orange-700 font-semibold flex items-center gap-2">
                  <ArrowLeft className="w-4 h-4" /> {lang === 'en' ? 'Back' : 'वापस'}
                </button>
                {!razorpayEnabled && (
                  <button onClick={confirmManualPayment} className="btn-orange rounded-full px-8 py-3 font-semibold">
                    {t(translations.puja.confirm)}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Order Summary sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-200 rounded-2xl p-5 sticky top-24">
            <h3 className="font-bold text-orange-800 mb-4 flex items-center gap-2">
              <ShoppingBag className="w-4 h-4" /> {lang === 'en' ? 'Order Summary' : 'संकल्प सारांश'}
            </h3>
            <div className="flex justify-between text-sm py-2 border-b border-orange-200">
              <span className="text-stone-700">{lang === 'en' ? pkg.name_en : pkg.name_hi} {lang === 'en' ? 'Package' : 'पैकेज'}</span>
              <span className="font-semibold">₹ {pkg.price}</span>
            </div>
            {panditDakshina && (
              <div className="flex justify-between text-sm py-2 border-b border-orange-200">
                <span className="text-stone-700">+ {lang === 'en' ? 'Pandit Ji Dakshina' : 'पंडित जी दक्षिणा'}</span>
                <span className="font-semibold shrink-0">₹ {PANDIT_DAKSHINA_PRICE}</span>
              </div>
            )}
            {Object.entries(selectedSamagri).filter(([_, v]) => v).map(([id]) => {
              const s = shopSamagri.find((x) => x.id === id);
              if (!s) return null;
              return (
                <div key={id} className="flex justify-between text-sm py-2 border-b border-orange-200">
                  <span className="text-stone-700 truncate pr-2">+ {lang === 'en' ? s.name_en : s.name_hi}</span>
                  <span className="font-semibold shrink-0">₹ {s.price}</span>
                </div>
              );
            })}
            {prasadDelivery && (
              <div className="flex justify-between text-sm py-2 border-b border-orange-200">
                <span className="text-stone-700">+ {lang === 'en' ? 'Prasad Home Delivery' : 'प्रसाद घर डिलीवरी'}</span>
                <span className="font-semibold shrink-0">₹ {PRASAD_DELIVERY_PRICE}</span>
              </div>
            )}
            {!panditDakshina && !prasadDelivery && Object.values(selectedSamagri).filter(Boolean).length === 0 && step >= 2 && (
              <p className="text-xs text-stone-500 py-2 italic">{lang === 'en' ? 'No add-ons yet' : 'अभी तक कोई add-on नहीं जोड़ा'}</p>
            )}
            <div className="flex justify-between mt-3 pt-3 border-t-2 border-orange-300">
              <span className="font-bold text-stone-900">{lang === 'en' ? 'Total' : 'कुल'}</span>
              <span className="text-2xl font-bold text-orange-700">₹ {total}</span>
            </div>
            <p className="text-[10px] text-stone-500 mt-3 leading-snug">
              {lang === 'en'
                ? '🙏 All offerings will be presented in your name during the Sankalp. Video recording included.'
                : '🙏 सभी अर्पण संकल्प में आपके नाम से होंगे। वीडियो रिकॉर्डिंग शामिल।'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
